import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Info } from 'lucide-react';
import { SbBrandLogoField, type SbBrandLogo } from '@/components/campaign-create/create-adgroup/SbBrandLogoField';
import { useToast } from '@/components/ui/Toast';
import { adsApi } from '@/api/client';
import { KeywordTargetingPanel } from '@/components/campaign-create/KeywordTargetingPanel';
import { NegativeKeywordTargetingSection } from '@/components/campaign-create/NegativeKeywordTargetingSection';
import { NegativeProductTargetingSection } from '@/components/campaign-create/NegativeProductTargetingSection';
import { ProductTargetingPanel } from '@/components/campaign-create/ProductTargetingPanel';
import { ManualTargetingKindFields } from '@/components/campaign-create/create-adgroup/ManualTargetingKindFields';
import { SbKeywordTargetingPanel } from '@/components/campaign-create/create-adgroup/SbKeywordTargetingPanel';
import { SbAdFormatCards } from '@/components/campaign-create/create-adgroup/SbAdFormatCards';
import { SbCollectionProducts } from '@/components/campaign-create/create-adgroup/SbCollectionProducts';
import { SbLandingPageField } from '@/components/campaign-create/create-adgroup/SbLandingPageField';
import { SbProductExclusionsSection } from '@/components/campaign-create/create-adgroup/SbProductExclusionsSection';
import { SbThemeTargetingGroups } from '@/components/campaign-create/create-adgroup/SbThemeTargetingGroups';
import { SpAdGroupSection } from '@/components/campaign-create/create-adgroup/SpAdGroupSection';
import {
  extractAdGroupMutationResults,
  extractSbKeywordCreateResults,
  formatCreateCampaignUpstreamError,
  parseBrandsPayload,
} from '@/utils/campaignCreate';
import { parseProductMetadataPayload } from '@/utils/spCampaignCreateDraft';
import { cn } from '@/utils/cn';
import {
  applySbThemeBidRecommendations,
  buildSbCollectionAd,
  buildSbKeywordPayloads,
  buildSbNegativeKeywordPayloads,
  buildSbNegativeProductPayloads,
  buildSbProductTargetPayloads,
  buildSbThemePayloads,
  collectionAsins,
  defaultSbAdGroupName,
  defaultSbCollectionAdName,
  defaultSbThemeTargets,
  extractSbAdCreateResults,
  parseSbThemeBidRecommendations,
  SB_BRAND_NAME_MAX,
  SB_COLLECTION_MIN_PRODUCTS,
  SB_HEADLINE_MAX,
  SB_THEME_TARGETS,
  validateSbCollectionAdGroupForm,
  type SbCollectionProductMode,
  type SbLandingPageKind,
  type SbThemeBidRecommendation,
} from '@/utils/sbAdGroupCreate';
import type {
  SpAddedKeyword,
  SpAddedNegativeBrand,
  SpAddedNegativeKeyword,
  SpAddedNegativeProduct,
  SpAddedProductTarget,
  SpAdvertisedProduct,
  SpManualTargetingKind,
} from '@/types/spCampaignCreate';

const SB_TARGETING_DEFAULT_BID = '1';
const EMPTY_KEYWORD_PRODUCTS: SpAdvertisedProduct[] = [];

export function CreateSbAdGroupForm({
  profileId,
  campaignId,
  onSuccess,
  onCancel,
}: {
  profileId: string;
  campaignId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [name, setName] = useState(() => defaultSbAdGroupName());
  const [productMode, setProductMode] = useState<SbCollectionProductMode>('automatic');
  const [products, setProducts] = useState<SpAdvertisedProduct[]>([]);
  const [adName, setAdName] = useState(() => defaultSbCollectionAdName());
  const [headline, setHeadline] = useState('');
  const [brandName, setBrandName] = useState('');
  const [logo, setLogo] = useState<SbBrandLogo | null>(null);
  const [landingKind, setLandingKind] = useState<SbLandingPageKind>('PRODUCT_LIST');
  const [storeId, setStoreId] = useState('');
  const [pageId, setPageId] = useState('');
  const [storePageUrl, setStorePageUrl] = useState('');
  const [targetingKind, setTargetingKind] = useState<SpManualTargetingKind>('KEYWORD');
  const [keywords, setKeywords] = useState<SpAddedKeyword[]>([]);
  const [themes, setThemes] = useState(() => defaultSbThemeTargets(SB_TARGETING_DEFAULT_BID));
  const [themeBidRecs, setThemeBidRecs] = useState<SbThemeBidRecommendation[]>([]);
  const [productTargets, setProductTargets] = useState<SpAddedProductTarget[]>([]);
  const [negativeKeywords, setNegativeKeywords] = useState<SpAddedNegativeKeyword[]>([]);
  const [negativeProducts, setNegativeProducts] = useState<SpAddedNegativeProduct[]>([]);
  const [negativeBrands, setNegativeBrands] = useState<SpAddedNegativeBrand[]>([]);
  const [excludedAsins, setExcludedAsins] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const brandsQuery = useQuery({
    queryKey: ['profile-brands', profileId],
    queryFn: async () => {
      const res = await adsApi.getBrands(profileId);
      if (!res.success) throw new Error(res.message || 'Failed to load Brand Registry brands');
      return parseBrandsPayload(res.data);
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  const catalogQuery = useQuery({
    queryKey: ['sb-keyword-seed-products', profileId],
    enabled: productMode === 'manual',
    queryFn: async () => {
      const res = await adsApi.getProductMetadata(profileId, {
        adType: 'SB',
        pageSize: 25,
        pageIndex: 0,
        checkItemDetails: false,
        checkEligibility: false,
      });
      if (!res.success) return [];
      return parseProductMetadataPayload(res.data);
    },
    staleTime: 60_000,
  });
  const collectionAsinsSelected = collectionAsins(products);
  const keywordProducts =
    collectionAsinsSelected.length > 0 ? products : catalogQuery.data ?? EMPTY_KEYWORD_PRODUCTS;
  const keywordSeedLoading =
    collectionAsinsSelected.length === 0 && !catalogQuery.data && catalogQuery.isPending;

  const brands = brandsQuery.data ?? [];
  const registeredBrandNames = brands
    .map((brand) => brand.brandRegistryName?.trim() ?? '')
    .filter(Boolean);

  useEffect(() => {
    if (registeredBrandNames.length === 0) return;
    const current = brandName.trim();
    // Always coerce account nicknames / stale free-text onto a Brand Registry name.
    // Otherwise the select can look fine while state still holds e.g. "LOOMANTHA miles to go...."
    // and the backend rejects before calling Amazon.
    if (!current || !registeredBrandNames.includes(current)) {
      setBrandName(registeredBrandNames[0]);
    }
  }, [brandName, registeredBrandNames]);

  const selectedBrand =
    brands.find((brand) => brand.brandRegistryName?.trim() === brandName.trim()) ?? brands[0];
  // Sellers need brandEntityId for /stores/assets; fall back to brandId for vendor rows.
  const brandEntityId =
    selectedBrand?.brandEntityId?.trim() || selectedBrand?.brandId?.trim() || '';

  useEffect(() => {
    if (productMode !== 'manual') return;
    let cancelled = false;
    const applyRecs = (recs: SbThemeBidRecommendation[]) => {
      if (cancelled || recs.length === 0) return false;
      setThemeBidRecs(recs);
      setThemes((current) => applySbThemeBidRecommendations(current, recs, SB_TARGETING_DEFAULT_BID));
      return true;
    };
    void (async () => {
      try {
        const sbRes = await adsApi.getSbKeywordBidRecommendations(profileId, {
          campaignId: campaignId.toString(),
          adFormat: 'productCollection',
          themes: SB_THEME_TARGETS.map((row) => ({ themeType: row.themeType })),
        });
        if (cancelled) return;
        if (sbRes.success) applyRecs(parseSbThemeBidRecommendations(sbRes.data));
      } catch {
        /* Theme bids stay on the default until Amazon returns ranges. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, productMode, profileId]);

  const invalidate = async () => {
    let syncRes;
    try {
      syncRes = await adsApi.syncCampaignCatalog(profileId, campaignId, 'SB');
    } catch (err) {
      throw new Error(
        `Ad group was created on Amazon, but saving it to the database failed. ${formatCreateCampaignUpstreamError(err)}`
      );
    }
    if (!syncRes.success) {
      throw new Error(
        syncRes.message || 'Ad group was created on Amazon, but it was not saved to the database yet.'
      );
    }
    await qc.invalidateQueries({ queryKey: ['sb-ad-groups', profileId, campaignId] });
    await qc.invalidateQueries({ queryKey: ['sb-ad-groups', profileId] });
    await qc.invalidateQueries({ queryKey: ['campaign-keywords-catalog'] });
  };

  const failAfterPartial = async (message: string) => {
    try {
      await invalidate();
      setSubmitError(message);
    } catch (err) {
      setSubmitError(`${message} ${formatCreateCampaignUpstreamError(err)}`);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    const resolvedHeadline =
      productMode === 'automatic' && !headline.trim()
        ? brandName.trim().slice(0, SB_HEADLINE_MAX)
        : headline;
    const error = validateSbCollectionAdGroupForm({
      name,
      adName,
      headline: resolvedHeadline,
      brandName,
      registeredBrandNames,
      brandsReady: brandsQuery.isFetched && !brandsQuery.isFetching,
      brandsLoadFailed: brandsQuery.isError,
      productMode,
      products,
      targetingKind: productMode === 'manual' ? targetingKind : 'KEYWORD',
      keywords,
      themes: productMode === 'manual' && targetingKind === 'KEYWORD' ? themes : [],
      productTargets: productMode === 'manual' && targetingKind === 'PRODUCT' ? productTargets : [],
      landingPageKind: productMode === 'manual' ? landingKind : 'PRODUCT_LIST',
      storePageUrl,
    });
    if (error) {
      setSubmitError(error);
      return;
    }

    setSubmitting(true);
    try {
      const agRes = await adsApi.createSbAdGroups(profileId, {
        adGroups: [{ campaignId: campaignId.toString(), name: name.trim(), state: 'ENABLED' }],
      });
      if (!agRes.success) {
        setSubmitError(agRes.message || 'Failed to create ad group');
        return;
      }
      const agParsed = extractAdGroupMutationResults(agRes.data);
      if (agParsed.errors.length > 0) {
        setSubmitError(agParsed.errors.join(' '));
        return;
      }
      const adGroupId = agParsed.successIds[0];
      if (!adGroupId) {
        setSubmitError('Ad group was created but no ad group ID was returned.');
        return;
      }

      const adsRes = await adsApi.createSbProductCollectionAds(profileId, {
        adFormat: productMode === 'automatic' ? 'autoCollection' : 'productCollection',
        ads: [
          buildSbCollectionAd({
            adGroupId,
            adName,
            headline: resolvedHeadline,
            brandName,
            productMode,
            products,
            brandLogoAssetId: logo?.assetId,
            landingPageKind: landingKind,
            storePageUrl,
          }),
        ],
      });
      if (!adsRes.success) {
        await failAfterPartial(`Ad group created, but the collection ad failed: ${adsRes.message || 'Unknown error'}`);
        return;
      }
      const adsParsed = extractSbAdCreateResults(adsRes.data);
      if (adsParsed.errors.length > 0 || adsParsed.successIds.length === 0) {
        await failAfterPartial(
          `Ad group created, but the collection ad failed: ${adsParsed.errors.join(' ') || 'No ad ID was returned.'}`
        );
        return;
      }

      const useKeywordTargets = productMode === 'automatic' || targetingKind === 'KEYWORD';
      if (useKeywordTargets && keywords.length > 0) {
        const kwRes = await adsApi.createSbKeywords(
          profileId,
          buildSbKeywordPayloads({ campaignId, adGroupId, keywords })
        );
        if (!kwRes.success) {
          await failAfterPartial(`Ad group created, but keywords failed: ${kwRes.message || 'Unknown error'}`);
          return;
        }
        const kwParsed = extractSbKeywordCreateResults(kwRes.data);
        if (kwParsed.errors.length > 0) {
          await failAfterPartial(`Ad group created, but keywords failed: ${kwParsed.errors.join(' ')}`);
          return;
        }
      }

      if (productMode === 'manual' && targetingKind === 'KEYWORD' && themes.length > 0) {
        const themeRes = await adsApi.createSbThemes(profileId, {
          themes: buildSbThemePayloads({ campaignId, adGroupId, themes }),
        });
        if (!themeRes.success) {
          await failAfterPartial(`Ad group created, but targeting groups failed: ${themeRes.message || 'Unknown error'}`);
          return;
        }
        const themeParsed = extractSbKeywordCreateResults(themeRes.data);
        if (themeParsed.errors.length > 0) {
          await failAfterPartial(`Ad group created, but targeting groups failed: ${themeParsed.errors.join(' ')}`);
          return;
        }
      }

      if (productMode === 'manual' && targetingKind === 'PRODUCT' && productTargets.length > 0) {
        const targetRes = await adsApi.createSbTargets(profileId, {
          targets: buildSbProductTargetPayloads({ campaignId, adGroupId, targets: productTargets }),
        });
        if (!targetRes.success) {
          await failAfterPartial(`Ad group created, but product targets failed: ${targetRes.message || 'Unknown error'}`);
          return;
        }
        const targetParsed = extractSbKeywordCreateResults(targetRes.data);
        if (targetParsed.errors.length > 0) {
          await failAfterPartial(`Ad group created, but product targets failed: ${targetParsed.errors.join(' ')}`);
          return;
        }
      }

      const negativeAsins = [
        ...(productMode === 'automatic' ? excludedAsins : []),
        ...(productMode === 'manual' && targetingKind === 'PRODUCT'
          ? negativeProducts.map((row) => row.asin)
          : []),
      ];
      const negativeBrandNames =
        productMode === 'manual' && targetingKind === 'PRODUCT'
          ? negativeBrands.map((row) => row.brand)
          : [];
      if (negativeAsins.length > 0 || negativeBrandNames.length > 0) {
        const exclusionRes = await adsApi.createSbNegativeTargets(profileId, {
          negative_targets: buildSbNegativeProductPayloads({
            campaignId,
            adGroupId,
            asins: negativeAsins,
            brands: negativeBrandNames,
          }),
        });
        if (!exclusionRes.success) {
          await failAfterPartial(
            `Ad group created, but negative products failed: ${exclusionRes.message || 'Unknown error'}`
          );
          return;
        }
        const exclusionResults = Array.isArray(
          (exclusionRes.data as { results?: unknown } | undefined)?.results
        )
          ? ((exclusionRes.data as { results: Array<{ status?: string; message?: string }> }).results)
          : [];
        const exclusionErrors = exclusionResults
          .filter((row) => row.status === 'error')
          .map((row) => row.message || 'Negative product failed.');
        if (exclusionErrors.length > 0) {
          await failAfterPartial(`Ad group created, but negative products failed: ${exclusionErrors.join(' ')}`);
          return;
        }
      }

      const negativePayload = buildSbNegativeKeywordPayloads({
        campaignId,
        adGroupId,
        keywords: productMode === 'automatic' || targetingKind === 'KEYWORD' ? negativeKeywords : [],
      });
      if (negativePayload.length > 0) {
        const negRes = await adsApi.createSbNegativeKeywords(profileId, {
          negative_keywords: negativePayload,
        });
        if (!negRes.success) {
          await failAfterPartial(
            `Ad group created, but negative keywords failed: ${negRes.message || 'Unknown error'}`
          );
          return;
        }
        const negParsed = extractSbKeywordCreateResults(negRes.data);
        if (negParsed.errors.length > 0) {
          await failAfterPartial(
            `Ad group created, but negative keywords failed: ${negParsed.errors.join(' ')}`
          );
          return;
        }
      }

      await invalidate();
      showToast('Ad group created', 'success');
      onSuccess();
    } catch (err) {
      setSubmitError(formatCreateCampaignUpstreamError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const showCreative =
    productMode === 'automatic' || collectionAsins(products).length >= SB_COLLECTION_MIN_PRODUCTS;

  return (
    <>
      <p className="text-sm text-foreground-muted mb-4">
        Creates a Sponsored Brands Collections ad group, collection ad, and keyword or product
        targeting via Amazon Ads APIs.
      </p>
      {submitError ? (
        <div className="mb-4 text-sm text-error bg-error-subtle border border-error/20 rounded-xl px-3 py-2">
          {submitError}
        </div>
      ) : null}
      <form onSubmit={submit} className="space-y-4">
        <SpAdGroupSection title="Ad group">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground-muted mb-1" htmlFor="sb-ad-group-name">
                Ad group name
              </label>
              <input
                id="sb-ad-group-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2"
                disabled={submitting}
              />
            </div>
            <SbAdFormatCards />
          </div>
        </SpAdGroupSection>

        <SpAdGroupSection title="Collection type">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label
              className={cn(
                'rounded-xl border p-3 cursor-pointer',
                productMode === 'automatic' ? 'border-brand bg-brand/5' : 'border-border'
              )}
            >
              <span className="flex items-start gap-2">
                <input
                  type="radio"
                  name="sb-collection-products"
                  className="mt-1"
                  checked={productMode === 'automatic'}
                  onChange={() => setProductMode('automatic')}
                  disabled={submitting}
                />
                <span>
                  <span className="block text-sm font-medium">Automatic</span>
                  <span className="block text-xs text-foreground-muted mt-1">
                    We&apos;ll create the most relevant collections of products from your catalogue,
                    based on your keyword targets and shopper queries.
                  </span>
                </span>
              </span>
            </label>
            <label
              className={cn(
                'rounded-xl border p-3 cursor-pointer',
                productMode === 'manual' ? 'border-brand bg-brand/5' : 'border-border'
              )}
            >
              <span className="flex items-start gap-2">
                <input
                  type="radio"
                  name="sb-collection-products"
                  className="mt-1"
                  checked={productMode === 'manual'}
                  onChange={() => setProductMode('manual')}
                  disabled={submitting}
                />
                <span>
                  <span className="block text-sm font-medium">Manual</span>
                  <span className="block text-xs text-foreground-muted mt-1">
                    Choose related products to feature in your collection.
                  </span>
                </span>
              </span>
            </label>
          </div>
        </SpAdGroupSection>

        {productMode === 'manual' ? (
          <SpAdGroupSection title="Products">
            <SbCollectionProducts
              profileId={profileId}
              products={products}
              onChange={setProducts}
              disabled={submitting}
            />
          </SpAdGroupSection>
        ) : null}

        {showCreative ? (
        <>
        {productMode === 'automatic' ? (
          <>
          <AutomaticCollectionAdSection
            profileId={profileId}
            brandEntityId={brandEntityId}
            adName={adName}
            brandName={brandName}
            registeredBrandNames={registeredBrandNames}
            brandsReady={brandsQuery.isFetched && !brandsQuery.isFetching}
            brandsLoadFailed={brandsQuery.isError}
            logo={logo}
            disabled={submitting}
            onAdNameChange={setAdName}
            onBrandNameChange={setBrandName}
            onLogoChange={setLogo}
          />
          <SbProductExclusionsSection
            asins={excludedAsins}
            onChange={setExcludedAsins}
            disabled={submitting}
          />
          </>
        ) : (
        <SpAdGroupSection title="Ad">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground-muted mb-1" htmlFor="sb-ad-name">
                Ad name
              </label>
              <input
                id="sb-ad-name"
                type="text"
                value={adName}
                onChange={(e) => setAdName(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-muted mb-1" htmlFor="sb-headline">
                Headline
              </label>
              <input
                id="sb-headline"
                type="text"
                value={headline}
                maxLength={SB_HEADLINE_MAX}
                onChange={(e) => setHeadline(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2"
                disabled={submitting}
                placeholder="Headline shoppers see on the ad"
              />
              <p className="text-xs text-foreground-subtle mt-1">
                {headline.length}/{SB_HEADLINE_MAX}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-muted mb-1" htmlFor="sb-brand-name">
                Brand name
              </label>
              {registeredBrandNames.length > 0 ? (
                <select
                  id="sb-brand-name"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  disabled={submitting}
                >
                  {registeredBrandNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="sb-brand-name"
                  type="text"
                  value={brandName}
                  maxLength={SB_BRAND_NAME_MAX}
                  autoComplete="off"
                  onChange={(e) => setBrandName(e.target.value)}
                  className="w-full border border-border rounded-lg px-3 py-2"
                  disabled={submitting}
                  placeholder={
                    brandsQuery.isPending || brandsQuery.isFetching
                      ? 'Loading brands…'
                      : 'e.g. VGR or IPARAH'
                  }
                />
              )}
              <p className="text-xs text-foreground-subtle mt-1">
                {brandsQuery.isError
                  ? 'Brand list timed out — type the exact Brand Registry name (VGR or IPARAH), not the account nickname.'
                  : 'Must match a Brand Registry name from this profile (not the account nickname).'}
              </p>
            </div>
            <SbBrandLogoField
              profileId={profileId}
              brandEntityId={brandEntityId}
              brandName={brandName}
              brandsReady={brandsQuery.isFetched}
              disabled={submitting}
              value={logo}
              onChange={setLogo}
            />
            <SbLandingPageField
              profileId={profileId}
              brandEntityId={brandEntityId}
              kind={landingKind}
              onKindChange={setLandingKind}
              storeId={storeId}
              onStoreIdChange={setStoreId}
              pageId={pageId}
              onPageIdChange={setPageId}
              onStorePageUrlChange={setStorePageUrl}
              disabled={submitting}
            />
          </div>
        </SpAdGroupSection>
        )}

        {productMode === 'automatic' ? (
          <SbKeywordTargetingPanel
            profileId={profileId}
            campaignId={campaignId}
            added={keywords}
            onChange={setKeywords}
            disabled={submitting}
          />
        ) : (
          <>
            <SpAdGroupSection title="Targeting">
              <ManualTargetingKindFields
                name="sb-manual-targeting-kind"
                value={targetingKind}
                onChange={setTargetingKind}
                disabled={submitting}
              />
            </SpAdGroupSection>
            {targetingKind === 'KEYWORD' ? (
              <>
                <SpAdGroupSection title="Keyword targeting">
                  <KeywordTargetingPanel
                    profileId={profileId}
                    campaignId={campaignId}
                    adProduct="SB"
                    landingUrl={landingKind === 'STORE' ? storePageUrl : undefined}
                    advertisedProducts={keywordProducts}
                    biddingStrategy="LEGACY_FOR_SALES"
                    defaultBid={SB_TARGETING_DEFAULT_BID}
                    added={keywords}
                    onChange={setKeywords}
                    disabled={submitting}
                    seedLoading={keywordSeedLoading}
                    framed={false}
                    extraAddedCount={themes.length}
                    onRemoveAll={() => {
                      setKeywords([]);
                      setThemes([]);
                    }}
                    onApplyAllSuggested={() => {
                      setThemes((current) =>
                        current.map((row) => {
                          const rec = themeBidRecs.find((item) => item.themeType === row.themeType);
                          const suggested = row.suggestedBid ?? rec?.suggestedBid ?? null;
                          if (suggested == null) return row;
                          return {
                            ...row,
                            suggestedBid: suggested,
                            suggestedBidMin: row.suggestedBidMin ?? rec?.suggestedBidMin ?? null,
                            suggestedBidMax: row.suggestedBidMax ?? rec?.suggestedBidMax ?? null,
                            bid: suggested.toFixed(2),
                          };
                        })
                      );
                    }}
                    suggestedLead={
                      <SbThemeTargetingGroups
                        layout="picker"
                        themes={themes}
                        onChange={setThemes}
                        defaultBid={SB_TARGETING_DEFAULT_BID}
                        disabled={submitting}
                        recommendations={themeBidRecs}
                      />
                    }
                    addedLead={
                      <SbThemeTargetingGroups
                        layout="added"
                        themes={themes}
                        onChange={setThemes}
                        defaultBid={SB_TARGETING_DEFAULT_BID}
                        disabled={submitting}
                        recommendations={themeBidRecs}
                      />
                    }
                  />
                </SpAdGroupSection>
                <NegativeKeywordTargetingSection
                  negativeKeywords={negativeKeywords}
                  onChange={setNegativeKeywords}
                  disabled={submitting}
                />
              </>
            ) : (
              <>
                <SpAdGroupSection title="Product targeting">
                  <ProductTargetingPanel
                    profileId={profileId}
                    campaignId={campaignId}
                    adProduct="SB"
                    advertisedProducts={keywordProducts}
                    defaultBid={SB_TARGETING_DEFAULT_BID}
                    added={productTargets}
                    onChange={setProductTargets}
                    disabled={submitting}
                  />
                </SpAdGroupSection>
                <NegativeProductTargetingSection
                  negativeProducts={negativeProducts}
                  onProductsChange={setNegativeProducts}
                  negativeBrands={negativeBrands}
                  onBrandsChange={setNegativeBrands}
                  disabled={submitting}
                />
              </>
            )}
          </>
        )}
        {productMode === 'automatic' ? (
          <NegativeKeywordTargetingSection
            negativeKeywords={negativeKeywords}
            onChange={setNegativeKeywords}
            disabled={submitting}
          />
        ) : null}
        </>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm px-3 py-1.5 rounded-lg border border-border text-foreground hover:bg-surface-muted"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="text-sm px-3 py-1.5 bg-brand text-brand-foreground rounded-lg hover:bg-brand-hover font-medium disabled:opacity-50"
            disabled={submitting}
          >
            {submitting ? 'Creating…' : 'Create ad group'}
          </button>
        </div>
      </form>
    </>
  );
}

const AUTOMATIC_COLLECTION_HELP =
  'https://advertising.amazon.com/library/guides/sponsored-brands-what-to-know';

function FieldLabel({
  htmlFor,
  children,
  hint,
}: {
  htmlFor: string;
  children: string;
  hint: string;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-foreground">
        {children}
      </label>
      <span title={hint} className="inline-flex text-foreground-subtle">
        <Info className="h-3.5 w-3.5" aria-hidden />
        <span className="sr-only">{hint}</span>
      </span>
    </div>
  );
}

function AutomaticCollectionAdSection({
  profileId,
  brandEntityId,
  adName,
  brandName,
  registeredBrandNames,
  brandsReady,
  brandsLoadFailed,
  logo,
  disabled,
  onAdNameChange,
  onBrandNameChange,
  onLogoChange,
}: {
  profileId: string;
  brandEntityId: string;
  adName: string;
  brandName: string;
  registeredBrandNames: string[];
  brandsReady: boolean;
  brandsLoadFailed: boolean;
  logo: SbBrandLogo | null;
  disabled: boolean;
  onAdNameChange: (value: string) => void;
  onBrandNameChange: (value: string) => void;
  onLogoChange: (logo: SbBrandLogo | null) => void;
}) {
  const brandTooLong = brandName.trim().length > SB_BRAND_NAME_MAX;

  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-medium text-foreground">Ad</h2>
        <a
          href={AUTOMATIC_COLLECTION_HELP}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-brand hover:underline"
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
          Learn about automatic collection ads
        </a>
      </div>

      <div className="mt-5">
        <FieldLabel htmlFor="sb-auto-ad-name" hint="Shoppers do not see the ad name.">
          Ad name
        </FieldLabel>
        <input
          id="sb-auto-ad-name"
          type="text"
          value={adName}
          onChange={(event) => onAdNameChange(event.target.value)}
          disabled={disabled}
          className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:border-brand"
        />
      </div>

      <div className="mt-5">
        <FieldLabel
          htmlFor="sb-auto-brand-name"
          hint="Must match Brand Registry exactly (not the account nickname). Amazon allows 30 characters."
        >
          Brand name
        </FieldLabel>
        {brandTooLong ? (
          <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-error">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
            Review and correct issues
          </p>
        ) : null}
        {registeredBrandNames.length > 0 ? (
          <select
            id="sb-auto-brand-name"
            value={brandName}
            onChange={(event) => onBrandNameChange(event.target.value)}
            disabled={disabled}
            aria-invalid={brandTooLong}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:border-brand"
          >
            {registeredBrandNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        ) : (
          <div className="relative">
            <input
              id="sb-auto-brand-name"
              type="text"
              value={brandName}
              autoComplete="off"
              onChange={(event) => onBrandNameChange(event.target.value)}
              disabled={disabled}
              aria-invalid={brandTooLong}
              placeholder={
                !brandsReady && !brandsLoadFailed ? 'Loading brands…' : 'e.g. VGR or IPARAH'
              }
              className={
                brandTooLong
                  ? 'h-10 w-full rounded-md border border-error bg-surface px-3 pr-9 text-sm outline-none'
                  : 'h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:border-brand'
              }
            />
            {brandTooLong ? (
              <AlertCircle className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-error" />
            ) : null}
          </div>
        )}
        {brandsLoadFailed && registeredBrandNames.length === 0 ? (
          <p className="mt-2 text-xs text-foreground-muted">
            Brand list timed out — type the exact registry name (VGR or IPARAH), not the account nickname.
          </p>
        ) : null}
      </div>

      <SbBrandLogoField
        profileId={profileId}
        brandEntityId={brandEntityId}
        brandName={brandName}
        brandsReady={brandsReady || brandsLoadFailed}
        disabled={disabled}
        value={logo}
        onChange={onLogoChange}
      />
    </section>
  );
}
